import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import { alpha, useTheme } from '@mui/material/styles';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';

/** Count chip styling aligned with AgentRankTabs `RankTabBadge` default variant */
function AppointmentTabChip({ count, selected }) {
  const theme = useTheme();

  return (
    <Chip
      label={count}
      size='small'
      sx={{
        height: 24,
        minWidth: 28,
        fontWeight: selected ? 600 : 500,
        fontSize: theme.typography.caption.fontSize,
        '& .MuiChip-label': { px: 1 },
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.15)
          : theme.palette.grey[300],
        color: selected ? theme.palette.primary.dark : theme.palette.grey[700],
        border: 'none'
      }}
    />
  );
}

AppointmentTabChip.propTypes = {
  count: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired
};

function AppointmentTabLabel({ labelText, count, selected, icon, loading }) {
  const theme = useTheme();

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='center'
      flexWrap='nowrap'
      spacing={1}
      useFlexGap
      sx={{
        typography: 'body2',
        fontWeight: selected ? 600 : 500,
        whiteSpace: 'nowrap',
        color: selected
          ? theme.palette.primary.main
          : theme.palette.text.secondary
      }}
    >
      {icon ? (
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: 'inherit',
            '& svg': {
              width: 18,
              height: 18,
              display: 'block'
            }
          }}
          aria-hidden='true'
        >
          {icon}
        </Box>
      ) : null}
      <span>{labelText}</span>
      {icon ? null : loading ? (
        <CircularProgress
          size={16}
          sx={{ color: 'inherit' }}
          aria-label={`Loading ${labelText} count`}
        />
      ) : (
        <AppointmentTabChip count={count} selected={selected} />
      )}
    </Stack>
  );
}

AppointmentTabLabel.propTypes = {
  labelText: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
  icon: PropTypes.node,
  loading: PropTypes.bool
};

function AgencyAppointmentsQuickFilters({
  tabs,
  value,
  onChange,
  counts,
  loadingByTab = {},
  ariaLabel = 'Quick filters'
}) {
  const handleChange = (_event, nextId) => {
    onChange(nextId);
  };

  return (
    <ScrollableTabs
      value={value}
      onChange={handleChange}
      aria-label={ariaLabel}
      tabPaddingY={1}
    >
      {tabs.map(({ id, label, icon }) => {
        const tabLoading = Boolean(loadingByTab[id]);
        return (
          <Tab
            key={id}
            value={id}
            disabled={tabLoading}
            label={
              <AppointmentTabLabel
                labelText={label}
                count={counts[id] ?? 0}
                selected={value === id}
                icon={icon}
                loading={tabLoading}
              />
            }
          />
        );
      })}
    </ScrollableTabs>
  );
}

AgencyAppointmentsQuickFilters.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  counts: PropTypes.objectOf(PropTypes.number).isRequired,
  loadingByTab: PropTypes.objectOf(PropTypes.bool),
  ariaLabel: PropTypes.string
};

export default AgencyAppointmentsQuickFilters;
